import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Flower2,
  ShoppingCart,
  Users,
  Bell,
  MessageSquare,
  Home,
  LogOut,
  X
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

/**
 * 관리자 사이드바
 * - 로그아웃 진입점 추가 (이전에는 전무) : useAuth().logout + localStorage 정리
 * - 로그인 관리자 정보 표시
 * - 모바일에서는 오프캔버스 형태로 동작 (isOpen / onClose / onLogout 는 AdminLayout 이 제어)
 * - aria-current 로 현재 메뉴 표시
 */
const Sidebar = ({ isOpen = false, onClose, onLogout, badges = {} }) => {
  const location = useLocation();
  const { user } = useAuth();

  const menuItems = [
    { to: '/admin/dashboard', icon: LayoutDashboard, label: '대시보드', exact: true },
    { to: '/admin/obituaries', icon: FileText, label: '부고 관리' },
    { to: '/admin/wreaths', icon: Flower2, label: '화환 상품 관리' },
    { to: '/admin/orders', icon: ShoppingCart, label: '주문 관리', badgeKey: 'orders' },
    { to: '/admin/users', icon: Users, label: '회원 관리' },
    { to: '/admin/notices', icon: Bell, label: '공지사항 관리' },
    { to: '/admin/inquiries', icon: MessageSquare, label: '문의 관리', badgeKey: 'inquiries' }
  ];

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleNavClick = () => {
    if (onClose) onClose();
  };

  return (
    <>
      {/* 모바일 오버레이 */}
      {isOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-40 flex w-64 flex-col bg-charcoal text-ivory
          transform transition-transform duration-200
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:static lg:translate-x-0 lg:min-h-screen
        `}
      >
        {/* 로고 + 모바일 닫기 */}
        <div className="flex items-center justify-between border-b border-gray-700 p-6">
          <Link to="/admin/dashboard" className="flex items-center space-x-2" onClick={handleNavClick}>
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-ivory">
              <span className="text-xl font-bold text-charcoal">관</span>
            </div>
            <div>
              <h1 className="text-xl font-bold">관리자</h1>
              <p className="text-sm text-gray-400">Admin Panel</p>
            </div>
          </Link>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-300 hover:text-ivory lg:hidden"
            aria-label="사이드바 닫기"
          >
            <X size={22} />
          </button>
        </div>

        {/* 메뉴 */}
        <nav className="flex-1 overflow-y-auto p-4" aria-label="관리자 메뉴">
          <ul className="space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.to, item.exact);
              const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;

              return (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    onClick={handleNavClick}
                    aria-current={active ? 'page' : undefined}
                    className={`
                      flex items-center justify-between rounded-lg px-4 py-3 transition-colors
                      ${active
                        ? 'bg-ivory font-semibold text-charcoal'
                        : 'text-gray-300 hover:bg-gray-700 hover:text-ivory'}
                    `}
                  >
                    <span className="flex items-center space-x-3">
                      <Icon size={20} />
                      <span>{item.label}</span>
                    </span>
                    {badgeCount > 0 && (
                      <span className="ml-2 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-red-500 px-1.5 py-0.5 text-xs font-bold text-white">
                        {badgeCount > 99 ? '99+' : badgeCount}
                      </span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* 하단: 관리자 정보 / 메인 사이트 / 로그아웃 */}
        <div className="space-y-1 border-t border-gray-700 p-4">
          {user && (
            <div className="mb-2 px-4 py-2 text-sm text-gray-400">
              <p className="truncate font-medium text-ivory">{user.name || '관리자'}</p>
              <p className="truncate text-xs">{user.email}</p>
            </div>
          )}
          <Link
            to="/"
            onClick={handleNavClick}
            className="flex items-center space-x-3 rounded-lg px-4 py-3 text-gray-300 transition-colors hover:bg-gray-700 hover:text-ivory"
          >
            <Home size={20} />
            <span>메인 사이트로</span>
          </Link>
          <button
            type="button"
            onClick={onLogout}
            className="flex w-full items-center space-x-3 rounded-lg px-4 py-3 text-gray-300 transition-colors hover:bg-red-600 hover:text-white"
          >
            <LogOut size={20} />
            <span>로그아웃</span>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
